import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import Tooltip from '@mui/material/Tooltip';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import AutoGraphRoundedIcon from '@mui/icons-material/AutoGraphRounded';
import Icon from '../shared/components/Icon';
import { usePagePresentationState } from '../context/PagePresentationContext';
import LanguageSwitcher from './LanguageSwitcher';
import DarkModeToggle from './DarkModeToggle';

// Renders page navigation, processing status, language, and theme actions.
export default function TopBar({ breadcrumbs = [], onOpenNavigation }) {
  const { t } = useTranslation();
  const { refreshLoading, estimation } = usePagePresentationState();
  const sourceYears = estimation.sourceYears || [];
  const hasEstimatedFactors = estimation.estimatedRecords > 0;
  const estimatedLabel = sourceYears.length === 1
    ? t('pageStatus.estimatedSingle', { year: sourceYears[0] })
    : t('pageStatus.estimatedMultiple');
  const estimatedTooltip = t('pageStatus.estimatedTooltip', {
    count: estimation.estimatedRecords,
    years: sourceYears.map(String).join(', ') || t('pageStatus.unknownYear'),
  });

  return (
    <header className="topbar">
      <div className="topbar-leading">
        <nav className="crumb" aria-label={t('breadcrumb.navigation')}>
          {onOpenNavigation && (
            <IconButton
              onClick={onOpenNavigation}
              aria-label={t('breadcrumb.openNavigation', { defaultValue: 'Open navigation' })}
              size="small"
              sx={{ display: { xs: 'inline-flex', md: 'none' }, flexShrink: 0 }}
            >
              <MenuRoundedIcon fontSize="small" />
            </IconButton>
          )}
          {breadcrumbs.map((item, index) => {
            const isLast = index === breadcrumbs.length - 1;
            const label = item.labelKey ? t(item.labelKey) : item.label;
            return (
              <React.Fragment key={`${item.path || 'current'}-${label}`}>
                {index > 0 && <Icon name="chevron-right" size={13} />}
                {item.path && !isLast ? (
                  <Link className="crumb-section crumb-link" to={item.path}>{label}</Link>
                ) : (
                  <span className={isLast ? 'crumb-page' : 'crumb-section'}>{label}</span>
                )}
              </React.Fragment>
            );
          })}
        </nav>
        {hasEstimatedFactors && (
          <div className="topbar-statuses">
            <Tooltip title={estimatedTooltip} arrow>
              <Chip
                className="topbar-status topbar-status--estimated"
                icon={<AutoGraphRoundedIcon />}
                label={estimatedLabel}
                size="small"
              />
            </Tooltip>
          </div>
        )}
      </div>
      <div className="topbar-actions">
        <LanguageSwitcher />
        <DarkModeToggle />
      </div>
      {refreshLoading && (
        <LinearProgress
          aria-label={t('common.loadingPage', { defaultValue: 'Loading page' })}
          className="topbar-progress"
        />
      )}
    </header>
  );
}
